import { useCallback, useEffect, useState } from "react";

import { closeTicket, getTicket, replyToTicket } from "../api/endpoints";
import { describeError } from "../api/errors";
import { newRequestId } from "../utils/requestId";
import type { Ticket, TicketMessage } from "../api/types";

interface TicketState {
  ticket: Ticket | null;
  messages: TicketMessage[];
  loading: boolean;
  error: string | null;
}

export interface UseTicket extends TicketState {
  sending: boolean;
  sendError: string | null;
  reply: (text: string, close: boolean) => Promise<boolean>;
  close: () => Promise<boolean>;
  refresh: () => void;
}

export function useTicket(ticketId: number | null): UseTicket {
  const [state, setState] = useState<TicketState>({
    ticket: null,
    messages: [],
    loading: false,
    error: null,
  });
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (ticketId === null) {
      setState({ ticket: null, messages: [], loading: false, error: null });
      return;
    }

    setState((previous) => ({ ...previous, loading: previous.ticket === null, error: null }));

    try {
      const thread = await getTicket(ticketId);
      setState({ ticket: thread.ticket, messages: thread.messages, loading: false, error: null });
    } catch (error) {
      setState((previous) => ({ ...previous, loading: false, error: describeError(error) }));
    }
  }, [ticketId]);

  useEffect(() => {
    void load();
  }, [load]);

  const reply = useCallback(
    async (text: string, close: boolean): Promise<boolean> => {
      if (ticketId === null || sending) {
        return false;
      }

      setSending(true);
      setSendError(null);
      try {
        await replyToTicket(ticketId, text, newRequestId(), close);
        await load();
        return true;
      } catch (error) {
        setSendError(describeError(error));
        // the answer is written on the api side whatever telegram did with it, so the thread
        // is reloaded even on a failure: it now carries the undelivered answer
        await load();
        return false;
      } finally {
        setSending(false);
      }
    },
    [ticketId, sending, load],
  );

  const close = useCallback(async (): Promise<boolean> => {
    if (ticketId === null || sending) {
      return false;
    }

    setSending(true);
    setSendError(null);
    try {
      await closeTicket(ticketId);
      await load();
      return true;
    } catch (error) {
      setSendError(describeError(error));
      return false;
    } finally {
      setSending(false);
    }
  }, [ticketId, sending, load]);

  return { ...state, sending, sendError, reply, close, refresh: () => void load() };
}
